import { useState } from "react";
import { useTranslation } from "react-i18next";
import { People, Calendar, DocumentText1, ArrowUp2, ArrowDown2 } from "iconsax-react";
import cn from "classnames";
import { EmptyState } from "@/shared/components";
import { FileViewer } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import type { SocialCategoryRecord } from "../../types/personalSheet";
import ProfileDataRow from "../ProfileDataRow";

interface Props {
  records: SocialCategoryRecord[];
  hasNoSocialCategories?: boolean;
}

export default function SocialCategoriesSection({ records, hasNoSocialCategories }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  if (records.length === 0) {
    if (hasNoSocialCategories) {
      return <p className="text-body-regular-md content-base-secondary">{t("personalSheet.noSocialCategoriesDeclared")}</p>;
    }
    return <EmptyState noDataText={t("personalSheet.empty.socialCategories")} noDataHint={t("personalSheet.empty.socialCategoriesHint")} compact />;
  }

  function toggleExpand(id: number) {
    setExpandedIds(function updateSet(prev) {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-3">
      {records.map(function renderRecord(record, index) {
        const isExpanded = expandedIds.has(record.id);
        const ArrowIcon = isExpanded ? ArrowUp2 : ArrowDown2;

        return (
          <div key={record.id} className="border surface-base-stroke radius-md overflow-hidden">
            <button
              type="button"
              onClick={() => toggleExpand(record.id)}
              className={cn("w-full px-4 py-3 flex items-center justify-between cursor-pointer", isExpanded && "pb-4 border-b surface-base-stroke")}>
              <span className="text-body-bold-md content-base-primary">
                {t("personalSheet.recordNumber", { number: index + 1 })} —{" "}
                {t(`personalSheet.socialCategoryValues.${record.category_type}`)}
              </span>
              <ArrowIcon size={16} color="currentColor" />
            </button>
            {isExpanded && (
              <div className="px-4 pb-4 flex flex-col gap-4">
                <div className="flex flex-col">
                  <ProfileDataRow icon={People} label={t("personalSheet.fields.categoryType")} value={t(`personalSheet.socialCategoryValues.${record.category_type}`)} />
                  <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.issueDate")} value={formatDateForDisplay(record.issue_date)} />
                  <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.expiryDate")} value={formatDateForDisplay(record.expiry_date)} />
                  <ProfileDataRow icon={DocumentText1} label={t("personalSheet.fields.notes")} value={record.notes} />
                </div>
                {record.document_file_url && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.documentFile")}</span>
                    <FileViewer existingFileUrl={record.document_file_url} isDownloadable />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
